import {createStateMaker} from '@shared/helpers/object'

import {
    ChargeBenefitPointRateNumber,
    MAXIMUM_POINT_BALANCE,
    MaximumUsingChargePointAmount,
    PayMoneyUserStatus,
    AccumulateClassTypeType,
    AccumulateClassType,
    ChargePointPolicy,
} from '@web/core/constants'

import {
    AccumulateRateBenefitCase,
    AccumulateState,
    ChargePointState,
    PaymoneyDisplayBankNameMap,
    PaymoneyMemberType,
} from './interfaces'

export const getInitialState = (): ChargePointState => {
    return {
        amount: 0,
        actualAmount: 0,
        nonCashMoneyAmount: 0,
        status: 'IDLE',
        apiResult: 'FAILED_RETRY_AVAILABLE',
        reloadable: {
            amount: 0,
            limitedType: null,
            apiResult: 'FAILED_RETRY_AVAILABLE',
            limit: null,
        },
        recharging: {
            amount: 0,
            accumulateStatus: AccumulateState.NORMAL,
            /**
             * 케이스별 충전포인트 적립률. 결제형만 가맹점별로 차등적용.
             * @see https://wiki.fin.navercorp.com/pages/viewpage.action?pageId=144233269
             * 기본적립 + 페이머니 사용 적립 + 그 외 케이스별 추가 적립 합산되어 적용.
             * (ex. 기본적립 1% + 페이머니 사용 적립 1.5% + 네이버 제휴통장 사용 0.5% = 3%)
             *
             * 주문형 충전결제 사용시 적립률 타입)
             * - 등록된 충전계좌가 없는 경우 MAX
             * - 충전계좌 전체 점검중인 경우 MAX
             * - 페이머니 회원인 경우 3
             * - 선택된 충전계좌가 네이버 통장인 경우 THREE
             * - 선택된 충전계좌가 네이버 통장이 아닌 경우 TWO_HALF
             * - 카드간편 : 3 (충전유도 배너에서만)
             * - 일반결제 : (충전결제 충전계좌 기준)네통 아닌 경우 2.5% or 네통인 경우 3
             * @ref(https://oss.fin.navercorp.com/payOrder/order-qa/issues/2288#issuecomment-359842)
             */
            accumulateRate: {
                [AccumulateRateBenefitCase.MAX]: ChargeBenefitPointRateNumber.MAX,
                [AccumulateRateBenefitCase.PAYMONEY_USER]: ChargeBenefitPointRateNumber.THREE,
                [AccumulateRateBenefitCase.NAVER_PARTNER_BANK_BOOK]: ChargeBenefitPointRateNumber.THREE,
                [AccumulateRateBenefitCase.DEFAULT]: ChargeBenefitPointRateNumber.TWO_HALF,
            },
            processingType: 'IMMEDIATE',
        },
        payMoney: {
            bankCode: PaymoneyMemberType.DEFAULT,
            bankName: PaymoneyDisplayBankNameMap[PaymoneyMemberType.DEFAULT],
            maskedAccount: '',
            userStatus: 'NOT_OPEN',
            apiResult: 'FAILED_RETRY_AVAILABLE',
        },
        requiredMethod: 'CHARGE_ACCOUNT',
        policy: ChargePointPolicy.DEFAULT,
        maximumHoldingPoint: MAXIMUM_POINT_BALANCE,
        maximumUsingAmount: null,
    }
}

const PayMoneyUserStatusList: PayMoneyUserStatusType[] = [
    PayMoneyUserStatus.OPEN,
    PayMoneyUserStatus.STOP_TRADING,
    PayMoneyUserStatus.EASY_BANK_DELETED,
]

export const isPayMoneyUser = (userStatus: PayMoneyUserStatusType) => {
    return PayMoneyUserStatusList.includes(userStatus)
}

export const makeMock = createStateMaker<ChargePointState>(getInitialState)

export const makeReloadableMock = createStateMaker<ChargePointState['reloadable']>(() => getInitialState().reloadable)

export const makePayMoneyMock = createStateMaker<ChargePointState['payMoney']>(() => getInitialState().payMoney)

export const makeRechargingMock = createStateMaker<ChargePointState['recharging']>(() => getInitialState().recharging)

export const overrideMockAsDeferred = (state: ChargePointState): ChargePointState => {
    return {
        amount: state.amount,
        actualAmount: state.actualAmount,
        nonCashMoneyAmount: state.nonCashMoneyAmount,
        status: state.status,
        apiResult: state.apiResult,
        reloadable: state.reloadable,
        recharging: {
            amount: state.recharging.amount,
            accumulateStatus: state.recharging.accumulateStatus,
            accumulateRate: state.recharging.accumulateRate,
            processingType: 'DEFERRED',
        },
        requiredMethod: 'CHARGE_ACCOUNT',
        payMoney: state.payMoney,
        maximumHoldingPoint: state.maximumHoldingPoint,
        maximumUsingAmount: state.maximumUsingAmount,
        policy: ChargePointPolicy.DEFAULT,
    }
}

export const overrideMockAsNonPayMoneyUser = (state: ChargePointState): ChargePointState => {
    return {
        amount: state.amount,
        actualAmount: state.actualAmount,
        nonCashMoneyAmount: state.nonCashMoneyAmount,
        status: state.status,
        apiResult: state.apiResult,
        reloadable: state.reloadable,
        recharging: state.recharging,
        payMoney: {
            apiResult: state.payMoney.apiResult,
            maskedAccount: state.payMoney.maskedAccount,
            userStatus: PayMoneyUserStatus.NOT_OPEN,
            bankCode: PaymoneyMemberType.DEFAULT,
            bankName: PaymoneyDisplayBankNameMap[PaymoneyMemberType.DEFAULT],
        },
        requiredMethod: 'CHARGE_ACCOUNT',
        maximumHoldingPoint: state.maximumHoldingPoint,
        maximumUsingAmount: state.maximumUsingAmount,
        policy: ChargePointPolicy.DEFAULT,
    }
}

export const overrideMockAsPayMoneyUser = (state: ChargePointState): ChargePointState => {
    return {
        amount: state.amount,
        actualAmount: state.actualAmount,
        nonCashMoneyAmount: state.nonCashMoneyAmount,
        status: state.status,
        apiResult: state.apiResult,
        reloadable: state.reloadable,
        recharging: state.recharging,
        payMoney: {
            apiResult: state.payMoney.apiResult,
            maskedAccount: '00******0004',
            userStatus: PayMoneyUserStatus.OPEN,
            bankCode: PaymoneyMemberType.HANA,
            bankName: PaymoneyDisplayBankNameMap[PaymoneyMemberType.HANA],
        },
        requiredMethod: 'CHARGE_ACCOUNT',
        maximumHoldingPoint: state.maximumHoldingPoint,
        maximumUsingAmount: state.maximumUsingAmount,
        policy: ChargePointPolicy.DEFAULT,
    }
}

export const overrideMockAsPayMoneyOnlyRequired = (state: ChargePointState): ChargePointState => {
    return {
        amount: state.amount,
        actualAmount: state.actualAmount,
        nonCashMoneyAmount: state.nonCashMoneyAmount,
        status: state.status,
        apiResult: state.apiResult,
        reloadable: state.reloadable,
        recharging: state.recharging,
        payMoney: state.payMoney,
        requiredMethod: 'PAY_MONEY_BALANCE',
        maximumHoldingPoint: state.maximumHoldingPoint,
        maximumUsingAmount: state.maximumUsingAmount,
        policy: ChargePointPolicy.DEFAULT,
    }
}

export const overrideMockAsAccumulateRateTwoPointFive = (state: ChargePointState): ChargePointState => {
    return {
        amount: state.amount,
        actualAmount: state.actualAmount,
        nonCashMoneyAmount: state.nonCashMoneyAmount,
        status: state.status,
        apiResult: state.apiResult,
        reloadable: state.reloadable,
        recharging: {
            amount: state.recharging.amount,
            accumulateStatus: state.recharging.accumulateStatus,
            processingType: state.recharging.processingType,
            accumulateRate: state.recharging.accumulateRate,
            expectedAccumulateRate: 2.5,
        },
        payMoney: state.payMoney,
        requiredMethod: state.requiredMethod,
        maximumHoldingPoint: state.maximumHoldingPoint,
        maximumUsingAmount: state.maximumUsingAmount,
        policy: ChargePointPolicy.DEFAULT,
    }
}

export const payMoneyMaxUsageAmount = ({
    paymentAmount,
    usingMoneyAmount,
    maximumMoneyChargeAmount,
    moneyAmount,
    isPayMoneyUser: isMoneyUser,
}: {
    paymentAmount: number
    usingMoneyAmount: number
    maximumMoneyChargeAmount: number
    moneyAmount: number
    isPayMoneyUser: boolean
}) => {
    /**
     * 머니 플레이트 선택시의 머니 최대 금액 (최대 200만원))
     * paymentAmount: 결제 필요 금액 (가입자: 상품 금액 - 포인트, 미가입자: 상품 금액 - 포인트 - 머니)
     * - 머니 가입자의 경우, 충전된 머니 금액을 항상 먼저 사용
     * - 머니 가입자가 아닌 경우, 충전된 머니 금액 사용량(usingMoneyAmount)을 조절할 수 있음
     */

    if (isMoneyUser) {
        return Math.min(Math.min(maximumMoneyChargeAmount + moneyAmount, paymentAmount), MaximumUsingChargePointAmount)
    }

    const chargeableAmount = Math.min(paymentAmount, maximumMoneyChargeAmount)
    return Math.min(chargeableAmount + usingMoneyAmount, MaximumUsingChargePointAmount)
}

/**
 * 충전한도 배열에서 특정 exceptionType을 가진 항목의 chargeableAmt를 반환
 * @param items 충전한도 배열
 * @param exceptionType 찾을 예외 타입 (e.g., 'MONTHLY_SUM_AMT', 'SINGLE_AMT')
 * @returns exceptionType과 일치하는 항목의 chargeableAmt, 없으면 첫 번째 항목, 그것도 없으면 0
 */
export const getChargeableAmtByExceptionType = (
    items: {chargeableAmt: number; exceptionType: Maybe<string>}[] | undefined,
    exceptionType: string,
): number => {
    return (
        items?.find((limit) => limit.exceptionType === exceptionType)?.chargeableAmt ?? items?.[0]?.chargeableAmt ?? 0
    )
}

interface Item {
    cpId: string
    itemId: string
    /** 총 결제 금액 */
    payAmount: number
    /** 적립 기준 금액 */
    acmBasisAmount: number
    /** 비동기 적립 적립예상일 */
    confirmExpectDays: number
    /** 적립 정보 */
    acmInfos: {
        /** 적립 유형 */
        accumulateClassType: Extract<
            AccumulateClassTypeType,
            'DIGITAL_CONTENTS_ACM' | 'CHARGE_ADD_ACM' | 'BANKBOOK_ACM'
        >
        acmAmount: number
        expectedAcmAmount: number
        acmRate: Maybe<number>
        acmName: Maybe<string>
    }[]
}

export const convertExpectRewardInfo = (
    items: Array<Item>,
): {expectedAccumulateRate: number; expectedReward: number} => {
    // 네이버 통장 적립금
    const expectedNaverBankbook = items.reduce((acc, item) => {
        const bankBookInfo = item.acmInfos.find(
            (acmInfo) => acmInfo.accumulateClassType === AccumulateClassType.BANKBOOK_ACM,
        )
        return acc + (bankBookInfo ? bankBookInfo?.expectedAcmAmount : 0)
    }, 0)

    // 머니 결제 적립금
    const expectedChargePoint = items.reduce((acc, item) => {
        const chargePointInfo = item.acmInfos.find(
            (acmInfo) => acmInfo.accumulateClassType === AccumulateClassType.CHARGE_ADD_ACM,
        )
        return acc + (chargePointInfo ? chargePointInfo?.expectedAcmAmount : 0)
    }, 0)
    // 디컨 기본 적립금
    const expectedDigitalContentAcm = items.reduce((acc, item) => {
        const digitalContentsInfo = item.acmInfos.find(
            (acmInfo) => acmInfo.accumulateClassType === AccumulateClassType.DIGITAL_CONTENTS_ACM,
        )
        return acc + (digitalContentsInfo ? digitalContentsInfo?.expectedAcmAmount : 0)
    }, 0)
    /** 디컨 기본 적립률 */
    const expectedDigitalContentAcmRate =
        items[0]?.acmInfos.find((acmInfo) => acmInfo.accumulateClassType === AccumulateClassType.DIGITAL_CONTENTS_ACM)
            ?.acmRate ?? 0

    /** 네이버 통장 적립률  */
    const expectedNaverBankbookAcmRate =
        items[0]?.acmInfos.find((acmInfo) => acmInfo.accumulateClassType === AccumulateClassType.BANKBOOK_ACM)
            ?.acmRate ?? 0
    /** 머니 결제 적립률 */
    const expectedChargeAcmRate =
        items[0]?.acmInfos.find((acmInfo) => acmInfo.accumulateClassType === AccumulateClassType.CHARGE_ADD_ACM)
            ?.acmRate ?? 0

    return {
        expectedAccumulateRate: expectedDigitalContentAcmRate + expectedChargeAcmRate + expectedNaverBankbookAcmRate,
        expectedReward: expectedDigitalContentAcm + expectedNaverBankbook + expectedChargePoint,
    }
}
